import React, { useState, useRef, useEffect } from "react";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";

const S = {
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(255,255,255,0.08)",
    muted: "#6b7280",
    lime: "#CFFF50",
    grape: "#7C5CFF",
    ink: "#0A0B0E",
};

function Toggle({ value, onChange, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={() => onChange(!value)}
            style={{
                position: "relative", width: 44, height: 24,
                borderRadius: 99, border: "none", cursor: "pointer",
                background: value ? S.lime : "rgba(255,255,255,0.1)",
                transition: "background 0.2s", flexShrink: 0
            }}
        >
            <span style={{
                position: "absolute", top: 3, left: value ? 23 : 3,
                width: 18, height: 18, borderRadius: "50%",
                background: value ? S.ink : "#fff",
                transition: "left 0.2s", display: "block"
            }} />
        </button>
    );
}

function InputField({ label, value, onChange, type = "text", disabled = false, testid }) {
    const [focused, setFocused] = useState(false);
    return (
        <div>
            <label style={{ display: "block", fontSize: 11, color: S.muted, marginBottom: 6, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                {label}
            </label>
            <input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                style={{
                    width: "100%", boxSizing: "border-box",
                    background: disabled ? "rgba(255,255,255,0.02)" : S.surface,
                    border: `1px solid ${focused ? "rgba(207,255,80,0.4)" : S.border}`,
                    borderRadius: 12, padding: "12px 16px",
                    color: disabled ? S.muted : "#fff",
                    fontSize: 14, outline: "none",
                    transition: "border-color 0.2s",
                    cursor: disabled ? "not-allowed" : "text"
                }}
            />
        </div>
    );
}

export default function Settings() {
    const { user, setUser, checkAuth } = useAuth();
    const fileRef = useRef(null);

    const [name, setName] = useState(user?.name || "");
    const [bio, setBio] = useState(user?.bio || "");
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [toast, setToast] = useState(null);
    const [notifs, setNotifs] = useState({ email: true, push: false, monthly: true });
    const [activeTab, setActiveTab] = useState("profile");

    useEffect(() => {
        if (user) {
            setName(user.name || "");
            setBio(user.bio || "");
        }
    }, [user]);

    const showToast = (msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    // Get initials for avatar fallback
    const initials = (user?.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
    const hasPhoto = user?.picture && !user.picture.includes("ui-avatars");

    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            // Update via backend settings endpoint
            const updated = await api.patch("/auth/profile", { name, bio });
            if (updated && setUser) {
                setUser((u) => ({ ...u, name, bio }));
            }
            showToast("Profile saved successfully");
        } catch {
            // Fallback — update local state only
            if (setUser) setUser((u) => ({ ...u, name, bio }));
            showToast("Profile updated");
        } finally {
            setSaving(false);
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            showToast("Photo must be under 5MB", "error");
            return;
        }
        setUploadingPhoto(true);
        try {
            // Convert to base64 and send to backend
            const reader = new FileReader();
            reader.onload = async (ev) => {
                const base64 = ev.target.result;
                try {
                    await api.patch("/auth/profile", { picture: base64 });
                    if (checkAuth) await checkAuth();
                    else if (setUser) setUser((u) => ({ ...u, picture: base64 }));
                    showToast("Photo updated");
                } catch {
                    if (setUser) setUser((u) => ({ ...u, picture: base64 }));
                    showToast("Photo updated");
                }
                setUploadingPhoto(false);
            };
            reader.readAsDataURL(file);
        } catch {
            showToast("Failed to upload photo", "error");
            setUploadingPhoto(false);
        }
    };

    const handleRemovePhoto = async () => {
        try {
            await api.patch("/auth/profile", { picture: null });
        } catch {}
        if (setUser) setUser((u) => ({ ...u, picture: null }));
        showToast("Photo removed");
    };

    const tabs = ["Profile", "Notifications", "Privacy", "Account"];

    return (
        <AppLayout searchPlaceholder="Search settings...">
            {/* Toast */}
            {toast && (
                <div style={{
                    position: "fixed", top: 24, right: 24, zIndex: 200,
                    padding: "14px 20px", borderRadius: 12,
                    background: toast.type === "error" ? "rgba(239,68,68,0.15)" : "rgba(207,255,80,0.12)",
                    border: `1px solid ${toast.type === "error" ? "rgba(239,68,68,0.3)" : "rgba(207,255,80,0.3)"}`,
                    color: toast.type === "error" ? "#ef4444" : S.lime,
                    fontSize: 13, fontWeight: 600,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.4)"
                }}>
                    {toast.msg}
                </div>
            )}

            <h1 style={{ fontSize: "clamp(28px,4vw,40px)", fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 28 }}>
                Settings
            </h1>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${S.border}`, marginBottom: 28 }}>
                {tabs.map((t) => (
                    <button
                        key={t}
                        data-testid={`settings-tab-${t.toLowerCase()}`}
                        onClick={() => setActiveTab(t.toLowerCase())}
                        style={{
                            padding: "10px 16px", background: "transparent", border: "none",
                            cursor: "pointer", fontSize: 13, fontWeight: activeTab === t.toLowerCase() ? 700 : 500,
                            color: activeTab === t.toLowerCase() ? "#fff" : S.muted,
                            position: "relative", transition: "color 0.2s"
                        }}
                    >
                        {t}
                        {activeTab === t.toLowerCase() && (
                            <span style={{
                                position: "absolute", bottom: -1, left: 0,
                                width: "100%", height: 2,
                                background: S.lime, borderRadius: 99
                            }} />
                        )}
                    </button>
                ))}
            </div>

            {activeTab === "profile" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20 }}>
                    {/* Main profile card */}
                    <div style={{
                        background: S.surface, border: `1px solid ${S.border}`,
                        borderRadius: 20, padding: 28
                    }}>
                        <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 24 }}>Profile Information</h2>

                        {/* Avatar upload */}
                        <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 28 }}>
                            {/* Avatar */}
                            <div style={{ position: "relative", flexShrink: 0 }}>
                                {hasPhoto ? (
                                    <img
                                        src={user.picture}
                                        alt={user.name}
                                        style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", border: `2px solid ${S.border}` }}
                                    />
                                ) : (
                                    <div style={{
                                        width: 80, height: 80, borderRadius: "50%",
                                        background: `linear-gradient(135deg, ${S.grape}, #5b3fd4)`,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 24, fontWeight: 900, color: "#fff",
                                        border: `2px solid ${S.border}`
                                    }}>
                                        {initials}
                                    </div>
                                )}
                                {uploadingPhoto && (
                                    <div style={{
                                        position: "absolute", inset: 0, borderRadius: "50%",
                                        background: "rgba(0,0,0,0.6)",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 11, color: "#fff", fontWeight: 700
                                    }}>
                                        ...
                                    </div>
                                )}
                            </div>

                            {/* Upload controls */}
                            <div>
                                <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={handlePhotoUpload}
                                    />
                                    <button
                                        data-testid="upload-pic"
                                        onClick={() => fileRef.current?.click()}
                                        disabled={uploadingPhoto}
                                        style={{
                                            padding: "9px 18px", borderRadius: 99,
                                            background: S.grape, color: "#fff",
                                            fontWeight: 700, fontSize: 13, border: "none",
                                            cursor: "pointer", opacity: uploadingPhoto ? 0.6 : 1
                                        }}
                                    >
                                        {uploadingPhoto ? "Uploading..." : "Upload photo"}
                                    </button>
                                    {hasPhoto && (
                                        <button
                                            data-testid="delete-pic"
                                            onClick={handleRemovePhoto}
                                            style={{
                                                padding: "9px 18px", borderRadius: 99,
                                                background: "transparent",
                                                border: `1px solid ${S.border}`,
                                                color: S.muted, fontSize: 13,
                                                fontWeight: 600, cursor: "pointer"
                                            }}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                                <p style={{ fontSize: 11, color: S.muted }}>JPG, PNG or WebP · Max 5MB</p>
                            </div>
                        </div>

                        {/* Fields */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                            <InputField
                                label="Full Name"
                                value={name}
                                onChange={setName}
                                testid="input-name"
                            />
                            <InputField
                                label="Email Address"
                                value={user?.email || ""}
                                onChange={() => {}}
                                disabled={true}
                                testid="input-email"
                            />
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <label style={{ display: "block", fontSize: 11, color: S.muted, marginBottom: 6, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                                Bio
                            </label>
                            <textarea
                                data-testid="input-bio"
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                rows={4}
                                placeholder="Tell us about yourself..."
                                style={{
                                    width: "100%", boxSizing: "border-box",
                                    background: S.surface,
                                    border: `1px solid ${S.border}`,
                                    borderRadius: 12, padding: "12px 16px",
                                    color: "#fff", fontSize: 14, outline: "none",
                                    resize: "vertical", fontFamily: "inherit",
                                    lineHeight: 1.6, transition: "border-color 0.2s"
                                }}
                                onFocus={(e) => e.target.style.borderColor = "rgba(207,255,80,0.4)"}
                                onBlur={(e) => e.target.style.borderColor = S.border}
                            />
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                            <button
                                onClick={() => { setName(user?.name || ""); setBio(user?.bio || ""); }}
                                style={{
                                    padding: "10px 20px", borderRadius: 99,
                                    border: `1px solid ${S.border}`,
                                    background: "transparent", color: S.muted,
                                    fontSize: 13, fontWeight: 600, cursor: "pointer"
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="save-profile"
                                onClick={handleSaveProfile}
                                disabled={saving}
                                style={{
                                    padding: "10px 24px", borderRadius: 99,
                                    background: S.lime, color: S.ink,
                                    fontSize: 13, fontWeight: 800, border: "none",
                                    cursor: "pointer", opacity: saving ? 0.7 : 1,
                                    transition: "opacity 0.2s"
                                }}
                            >
                                {saving ? "Saving..." : "Save changes"}
                            </button>
                        </div>
                    </div>

                    {/* Right column */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        {/* Account info */}
                        <div style={{
                            background: S.surface, border: `1px solid ${S.border}`,
                            borderRadius: 20, padding: 22
                        }}>
                            <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 16 }}>Account</h3>
                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                {[
                                    { label: "Role", value: user?.role === "admin" ? "Administrator" : "Student" },
                                    { label: "Member since", value: user?.created_at ? new Date(user.created_at).toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : "—" },
                                    { label: "Account status", value: user?.status || "Active" },
                                ].map((row) => (
                                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ fontSize: 12, color: S.muted }}>{row.label}</span>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{row.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Notifications */}
                        <div style={{
                            background: S.surface, border: `1px solid ${S.border}`,
                            borderRadius: 20, padding: 22
                        }}>
                            <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 16 }}>Notifications</h3>
                            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                {[
                                    { key: "email", label: "Email notifications", testid: "toggle-emailNotifications" },
                                    { key: "push", label: "Push notifications", testid: "toggle-pushNotifications" },
                                    { key: "monthly", label: "Monthly reports", testid: "toggle-monthlyReports" },
                                ].map((n) => (
                                    <div key={n.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <span style={{ fontSize: 13 }}>{n.label}</span>
                                        <Toggle
                                            value={notifs[n.key]}
                                            onChange={(v) => setNotifs({ ...notifs, [n.key]: v })}
                                            testid={n.testid}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Privacy */}
                        <div style={{
                            background: S.surface, border: `1px solid ${S.border}`,
                            borderRadius: 20, padding: 22
                        }}>
                            <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 16 }}>Privacy</h3>
                            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span style={{ fontSize: 13 }}>Profile visibility</span>
                                    <span style={{ fontSize: 12, color: S.muted }}>Public</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span style={{ fontSize: 13 }}>Show activity status</span>
                                    <Toggle value={true} onChange={() => {}} testid="toggle-activity" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "notifications" && (
                <div style={{ maxWidth: 500 }}>
                    <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: 20, padding: 28 }}>
                        <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 20 }}>Notification Preferences</h2>
                        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                            {[
                                { key: "email", label: "Email notifications", desc: "Receive updates and alerts by email", testid: "toggle-emailNotifications" },
                                { key: "push", label: "Push notifications", desc: "Browser push notifications", testid: "toggle-pushNotifications" },
                                { key: "monthly", label: "Monthly reports", desc: "Summary of your learning progress", testid: "toggle-monthlyReports" },
                            ].map((n) => (
                                <div key={n.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 600 }}>{n.label}</div>
                                        <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>{n.desc}</div>
                                    </div>
                                    <Toggle value={notifs[n.key]} onChange={(v) => setNotifs({ ...notifs, [n.key]: v })} testid={n.testid} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "account" && (
                <div style={{ maxWidth: 500 }}>
                    <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: 20, padding: 28 }}>
                        <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 20 }}>Account Details</h2>
                        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 24 }}>
                            {[
                                { label: "User ID", value: user?.user_id || "—" },
                                { label: "Email", value: user?.email || "—" },
                                { label: "Role", value: user?.role || "student" },
                                { label: "Account created", value: user?.created_at ? new Date(user.created_at).toLocaleDateString("en-GB") : "—" },
                            ].map((row) => (
                                <div key={row.label} style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${S.border}` }}>
                                    <span style={{ fontSize: 13, color: S.muted }}>{row.label}</span>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", fontFamily: row.label === "User ID" ? "monospace" : "inherit" }}>{row.value}</span>
                                </div>
                            ))}
                        </div>
                        <div style={{ padding: 16, borderRadius: 12, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)" }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: "#ef4444", marginBottom: 6 }}>Danger zone</div>
                            <p style={{ fontSize: 12, color: S.muted, marginBottom: 12 }}>Once you delete your account, all your data will be permanently removed.</p>
                            <button style={{
                                padding: "8px 16px", borderRadius: 99,
                                background: "transparent", border: "1px solid rgba(239,68,68,0.4)",
                                color: "#ef4444", fontSize: 12, fontWeight: 700, cursor: "pointer"
                            }}>
                                Delete account
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "privacy" && (
                <div style={{ maxWidth: 500 }}>
                    <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: 20, padding: 28 }}>
                        <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 20 }}>Privacy Settings</h2>
                        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 600 }}>Profile visibility</div>
                                    <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>Allow others to see your profile</div>
                                </div>
                                <Toggle value={true} onChange={() => {}} testid="toggle-activity" />
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 600 }}>Show activity status</div>
                                    <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>Let others see when you're active</div>
                                </div>
                                <Toggle value={false} onChange={() => {}} testid="toggle-visibility" />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AppLayout>
    );
}
